import {
	Alert,
	AlertTitle,
	Box,
	Card,
	CardActions,
	CardContent,
	IconButton,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material"
import FlagIcon from "@mui/icons-material/Flag"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
	resetWorkingArticleNoteRelation,
	selectWorkingArticleNoteRelation,
	setWorkingArticleNoteRelationAsync,
} from "../../../reducers/workingArticleNoteRelationSlice"
import ArticleBreadcrumbs from "./ArticleBreadcrumbs"
import BreadCrumbMenu from "../BreadcrumbMenu"
import { useContextMenu } from "react-contexify"
import {ArticleSelectionBox} from "./ArticleSelectionBox"
import {selectSelectedArticles} from "../../../reducers/selectedArticlesSlice"
import {DownloadForOffline, PictureAsPdf} from "@mui/icons-material"
import {ArticleFileList} from "../fileUpload/ArticleFileList"
import ArticleCitation from "./ArticleCitation"
import { addArticle } from "../../../reducers/recentlyReviewedSlice"
import { fetchArticleBreadcrumbs, selectArticleBreadcrumbs } from "../../../reducers/breadcrumbSlice"
import { selectTargetArticle, setTargetArticle, clearState } from "../../../reducers/targetArticleSlice"
import axios from "axios"
import DiscreteOpenButton from "../../../utils/DiscreteOpenButton"
import ArticleLinksAndControls from "./ArticleLinksAndControls"
import {useWorkspaceContext} from "../../../pages/Dashboard"
import {getRecentArticlesKey} from "../../../utils/reduxUtilities"
import {getArticleTargetKey} from "../../../utils/articleUtilities"

export default function Article({
	article,
	children,
	isDragFilesActive,
	isImported,
	isExcluded,
	articleWorkspace,
	isUnassigned,
	index,
	fullText,
	openFullText,
	files,
	setFullTextId,
	setArticleFiles,
	handleUploadButtonClick,
	fromAutoSearch
}) {
	// NOTE: this value is never going to be Article.id, the PK generated by our DB
	// the value should only be used to set and compare to the TargetArticleSlice.
	const id = getArticleTargetKey(article)

	const theme = useTheme()
	const scrollRef = useRef(null)
	const { show, hideAll } = useContextMenu({
		id: "breadcrumb_menu",
	})

	const dispatch = useDispatch()
	const {workspaceId} = useWorkspaceContext()
	const articleRelations = useSelector((state) => selectWorkingArticleNoteRelation(state, workspaceId))
	const selectedArticles = useSelector((state) => selectSelectedArticles(state, workspaceId))

	const selectedBreadcrumbs = useSelector((state) => selectArticleBreadcrumbs(state, workspaceId))
	const breadcrumbs = selectedBreadcrumbs[article.id]

	const [copyTextMode, setCopyTextMode] = useState(false)
	const [isHovered, setIsHovered] = useState(false)

	const targetArticle = useSelector((state) => selectTargetArticle(state, workspaceId))
	const setTargetObject = (id) => {
		dispatch(setTargetArticle({id, workspaceId}))
	}

	const fullTextFile = files && files.length && fullText && files.find(file => file.id === fullText)

	const downloadNonPdfFullText = async () => {
		// these can only exist on ImportedArticles, so we can assume that article.id !== undefined
		const {data: fileUrl} = await axios.get(`/api/workspace/${workspaceId}/article/${article.id}/attachment/${fullTextFile.id}`)
		axios
			.get(fileUrl, {
				// in App.js we set the Axios' default Headers to always include
				// JWT auth from auth0, and we need to remove those before
				// making an S3 req.
				transformRequest: (data, headers) => {
					delete headers.common["Authorization"]
					return data
				},
				responseType: "blob",
			})
			.then(({data}) => {
				const fakeHref = URL.createObjectURL(data)
				const fakeEl = document.createElement("a")
				fakeEl.setAttribute("download", fullTextFile.fileName)
				fakeEl.href = fakeHref
				fakeEl.setAttribute("target", "_blank")
				fakeEl.click()
				URL.revokeObjectURL(fakeHref)
			})
			.catch((err) => {
				console.error("DOWNLOADING FULL-TEXT FAILED: ", err)
			})
	}


	useEffect(() => {
		if (id === targetArticle) {
			scrollRef.current.scrollIntoView({
				behavior: "smooth",
				block: "start",
			})
		}
	}, [id, targetArticle])

	useEffect(() => {
		if (article.id && targetArticle === getArticleTargetKey(article)) {
			dispatch(fetchArticleBreadcrumbs({article, workspaceId}))
		}
	}, [targetArticle, article, workspaceId, dispatch])

	const addToRecentlyViewed = () => {
		if (!isImported) return

		const recentlyViewedArticlesKeyForWorkspace = getRecentArticlesKey(workspaceId)

		let recentlyViewed = JSON.parse(
			localStorage.getItem(recentlyViewedArticlesKeyForWorkspace) || "[]"
		)

		const i = recentlyViewed.findIndex((ra) => {
			return ra.articleId === id
		})

		if (i !== -1) {
			recentlyViewed.splice(i, 1)
		}

		recentlyViewed.unshift(articleWorkspace)
		localStorage.setItem(
			recentlyViewedArticlesKeyForWorkspace,
			JSON.stringify(recentlyViewed.slice(0, 20))
		)
		dispatch(addArticle({articleWorkspace, workspaceId}))
	}


	const handleClick = () => {
		addToRecentlyViewed()
		if (id !== targetArticle) {
			setTargetObject(id)
			if (isImported && !isUnassigned) {
				// if isImported, we can assume article.id !== undefined
				dispatch(setWorkingArticleNoteRelationAsync(workspaceId, article.id))
			}
		} else {
			dispatch(clearState(workspaceId))
			if(isImported && !isUnassigned) {
				dispatch(resetWorkingArticleNoteRelation(workspaceId))
			}
		}
		scrollRef.current.scrollIntoView({
			behavior: "smooth",
			block: "start",
		})
	}

	const handleContextMenu = (e) => {
		show(e, { position: null })
	}

	const getYearFromPublicationDate = (pubDateString) => {
		const pubDate = new Date(pubDateString)
		return pubDate.toLocaleDateString("en-US", { year: "numeric" })
	}

	return (
		<Card
			ref={ scrollRef }
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			elevation={0}
			variant={isExcluded ? "excludedArticle" : "note"}
			sx={{
				display: "flex",
				borderRadius: "0px",
				marginBottom: "2px",
				boxSizing: "border-box",
				position: "relative",
				"&:hover": {
					boxShadow:
						id === targetArticle || isExcluded
							? "none"
							: "0 0px 8px rgba(0, 0, 0, 0.7)",
					backgroundColor:
						id === targetArticle && !isExcluded
							? "notes.background"
							: !isExcluded && "#f0f0f0"
				},
			}}
		>
			{ (isImported || id === targetArticle) &&
				<ArticleSelectionBox
					isHovered={isHovered}
					isOpen={id === targetArticle}
					selectedArticles={selectedArticles}
					articleWorkspace={articleWorkspace}
					isImported={isImported}
					id={id}
				/>
			}
			<CardActions sx={{ padding: 0 }}>{children}</CardActions>
			<CardContent
				sx={{
					transition: "background-color 0.1s ease-in",
					...(isDragFilesActive && {
						backgroundColor: theme.palette.grey["200"],
						transition: "background-color 0.1s ease-out",
					}),
					padding: 0,
					"&:last-child": {
						padding: 0,
					},
				}}
			>
				<Box sx={{
					display: "flex",
					flexDirection: "row"
				}}>
					<Box sx={{
						paddingTop: "10px",
						paddingRight: "5px",
						flex: "1 0 auto",
						display: "flex",
						flexDirection: "column",
						alignItems: "end",
						minWidth: "24px"
					}} >
						<Typography
							variant={"articleIndex"}
							component={"div"}
							sx={{
								color: "RBOneFont.otherInfo",
								fontSize: "1rem",
								lineHeight: "1.4",
								fontWeight: 700,
								paddingLeft: "5px"
							}}
						>
							{index + 1}
						</Typography>
						{!!fullText && !!fullTextFile && (
							<Box>
								{fullTextFile.fileType === "pdf" ? (
									<Tooltip title={"Open PDF"}>
										<IconButton
											onClick={() => openFullText() }
											sx={{
												paddingTop: "3px",
												color: "dragBar.pdfIcon",
												"&:hover": {
													backgroundColor: "#eaeaea",
													color: "dragBar.pdfHover",
												},
											}}
										>
											<PictureAsPdf fontSize="small" />
										</IconButton>
									</Tooltip>
								) : (
									<Tooltip title={"Download File"}>
										<IconButton
											onClick={downloadNonPdfFullText}
											sx={{
												paddingTop: "3px",
												color: "dragBar.importROne",
												"&:hover": {
													backgroundColor: "#eaeaea",
													color: "dragBar.importROneHover",
												},
											}}
										>
											<DownloadForOffline fontSize="small" />
										</IconButton>
									</Tooltip>
								)}
							</Box>
						)}
					</Box>
					{id !== targetArticle &&
						<DiscreteOpenButton
							onClick={handleClick}
							variant={"text"}
							color={"otherInfo"}
							sx={{
								backgroundColor: "none",
								fontWeight: "bold",
								boxSizing: "border-box",
								marginRight: "30px"
							}}
						>
							Open Citation
						</DiscreteOpenButton>
					}
					<Stack spacing={0.5}>
						{id !== targetArticle ? (
							<Box
								className={"citation-container"}
								sx={{
									width: "100%",
									marginBottom: "6px"
								}}
							>
								<ArticleCitation
									id={id}
									targetArticle={targetArticle}
									article={article}
									isUnassigned={isUnassigned}
									getYearFromPublicationDate={getYearFromPublicationDate}
								/>
							</Box>
						) : (
							<Box
								sx={{
									width: "100%",
								}}
							>
								<ArticleCitation
									id={id}
									targetArticle={targetArticle}
									article={article}
									isUnassigned={isUnassigned}
									getYearFromPublicationDate={getYearFromPublicationDate}
								/>
							</Box>
						)}
						<ArticleLinksAndControls
							article={article}
							selected={id === targetArticle}
							fullText={fullText}
							setFullTextId={setFullTextId}
							setArticleFiles={setArticleFiles}
							fromAutoSearch={fromAutoSearch}
							articleWorkspace={articleWorkspace}
						/>
						{id === targetArticle && (
							<Box>
								{
									(isUnassigned && id === targetArticle && (!breadcrumbs || breadcrumbs?.length===0)) && (
										<Alert
											sx={{
												backgroundColor: "unassignedArticle.main",
												margin: "8px 8px 0 8px",
												color: "RBOneFont.main",
												padding: "0 16px",
											}}
											icon={<FlagIcon sx={{color: "RBOneFont.main"}} />}
										>
										
											<AlertTitle sx={{marginBottom: "-2px"}}>
												<strong>Unattached</strong>
											</AlertTitle>
											<Typography
												variant={"subtitle2"}
											>
												Drag the green status indicator bar and drop it on a heading in the left panel that describes its content.
											</Typography>
										</Alert>
									)
								}
								{breadcrumbs && breadcrumbs.length > 0 && (
									<Box sx={{ position: "relative" }}>
										<Box
											sx={{
												padding: "8px",
												borderRadius: "5px",
												marginBottom: "10px",
											}}
										>
											{breadcrumbs.map((note) => (
												<ArticleBreadcrumbs
													key={note.id}
													note={note}
													articleRelations={articleRelations}
													workspaceId={workspaceId}
													articleId={article.id}
													copyTextMode={copyTextMode}
													handleContextMenu={handleContextMenu}
													hide={hideAll}
												/>
											))}
										</Box>
										<BreadCrumbMenu
											setCopyTextMode={setCopyTextMode}
											copyTextMode={copyTextMode}
											hide={hideAll}
										/>
									</Box>
								)}
								<br />
								<Typography sx={{ padding: "8px" }}>
									{article.abstract || "No abstract available"}
								</Typography>
								{isImported && (
									<ArticleFileList
										files={files}
										articleWorkspace={articleWorkspace}
										fullTextId={fullText}
										setArticleFiles={setArticleFiles}
										handleUploadButtonClick={handleUploadButtonClick}
									/>
								)}
							</Box>
						)}
					</Stack>
				</Box>
			</CardContent>
		</Card>
	)
}